import React, { useState, useEffect, useRef } from "react";
import "./FileTransfer.scss";
import InstructionMenu from "../InstructionMenu/InstructionMenu.jsx"
import ProgressBar from "../Common/ProgressBar/ProgressBar.jsx"

const API_URL = process.env.REACT_APP_API_URL ;
const WS_URL  = process.env.REACT_APP_WS_URL  ;

const FileTransfer = ({ actionText, serialNumber, companyName, instructions }) => {
  const [files, setFiles]               = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [progress, setFetchProgress]    = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [displayedUploadProgress, setDisplayedUploadProgress] = useState(0);
  const [customFilename, setCustomFilename] = useState("");
  const [workOrder, setWorkOrder]       = useState("");
  const [logs, setLogs]                 = useState([]);
  const [sftpLegs, setSftpLegs]         = useState({});
  const [logOpen, setLogOpen]           = useState(true);
  const [transferComplete, setTransferComplete] = useState(false);
  let wsRef = useRef(null);
  const logEndRef = useRef(null);
  const lastReadMilestoneRef = useRef(-1);
  const lastUploadFloorRef = useRef(-1);
  // The server broadcasts progress to every connected browser, so each upload
  // gets a unique id and frames tagged with someone else's id are ignored —
  // otherwise two techs uploading at once corrupt each other's bars.
  const uploadIdRef = useRef(null);
  const displayedProgressRef = useRef(0);

  const logMessage = (msg) => {
    console.log(msg);
    setLogs((prev) => [...prev, msg]);
  };

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Azure App Service's proxy silently drops WebSocket connections after a few
  // minutes of inactivity. Without a reconnect, a page left open (typing tail
  // number, waiting for the pocketReader read) has a dead socket by upload time
  // and never receives progress — so this keeps retrying until it's back.
  useEffect(() => {
    let disposed = false;
    let reconnectTimer = null;

    const connect = () => {
      if (disposed) return;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      let opened = false;

      ws.onopen = () => {
        opened = true;
        logMessage("Connected to server");
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);

        // Drop frames tagged with another client's upload id (untagged frames
        // are from an older backend and pass through for compatibility).
        if (data.uploadId && data.uploadId !== uploadIdRef.current) return;

        // "uploadProgress" frames report the server→Azure leg, which runs in
        // under a second in production (same datacenter) — they all arrive in
        // a burst at the end, so they no longer drive the bar. The bar tracks
        // the browser→server leg via XHR in uploadFile(); the cloud leg shows
        // up here as log milestones instead.
        // Partner SFTP legs (PSA uploads only — no other company emits these).
        // Each leg's row appears the moment its first frame lands.
        if (data.type === "sftpProgress") {
          setSftpLegs((prev) => ({
            ...prev,
            [data.leg]: {
              leg: data.leg, order: data.order,
              percent: data.percent, state: data.state,
            },
          }));
          return;
        }

        if (data.type === "uploadLog") {
          logMessage(data.message);
        }
      };

      ws.onclose = () => {
        if (disposed) return;
        // Only log drops of a live connection — failed retries stay quiet
        // so the log panel doesn't fill up while the server is unreachable.
        if (opened) logMessage("Server connection lost — reconnecting...");
        reconnectTimer = setTimeout(connect, 2000);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, []);

  // Backend progress arrives in coarse, uneven jumps (once per 4MB chunk).
  // Animate the displayed bar so it creeps toward the real value instead of
  // snapping, by chasing `uploadProgress` at a capped speed every frame.
  // The loop stops once the bar catches up (and restarts when uploadProgress
  // changes) so an idle page isn't burning CPU at 60fps.
  useEffect(() => {
    const maxPercentPerSecond = 80;
    let lastTime = null;
    let frameId = null;

    const step = (now) => {
      if (lastTime === null) lastTime = now;
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      const current = displayedProgressRef.current;
      const diff = uploadProgress - current;
      const next = Math.abs(diff) < 0.05
        ? uploadProgress
        : current + Math.sign(diff) * Math.min(Math.abs(diff), maxPercentPerSecond * dt);

      displayedProgressRef.current = next;
      setDisplayedUploadProgress(next);

      frameId = next !== uploadProgress ? requestAnimationFrame(step) : null;
    };

    frameId = requestAnimationFrame(step);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [uploadProgress]);

  useEffect(() => {
    if (
      !transferComplete &&
      parseFloat(progress) === 100 &&
      uploadProgress >= 100 &&
      displayedUploadProgress >= 99.5
    ) {
      setTransferComplete(true);
      alert("Transfer Complete.");
    }
  }, [progress, uploadProgress, displayedUploadProgress, transferComplete]);

  const handleFileChange = (event) => {
    setSelectedFile(null);
    setUploadProgress(0);
    setDisplayedUploadProgress(0);
    setFetchProgress(0);
    setTransferComplete(false);
    setLogs([]);
    setSftpLegs({});
    setCustomFilename("");
    setWorkOrder("");
    lastReadMilestoneRef.current = -1;
    lastUploadFloorRef.current = -1;
    displayedProgressRef.current = 0;
    uploadIdRef.current = null;

    const file = event.target.files[0];
    if (file && customFilename.trim() !== "") {
      const extension = file.name.split(".").pop();
      const newName   = `${customFilename}.${extension}`;
      const renamedFile = new File([file], newName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      setSelectedFile(renamedFile);
      logMessage(`Selected: ${renamedFile.name} (${(renamedFile.size / 1024).toFixed(2)} KB)`);

      setTimeout(() => chunkRead(renamedFile), 500);
    } else if (!file) {
      alert("Invalid file. Try again.");
    }
  };

  const chunkRead = (file) => {
    const chunkSize = 4096;
    let offset = 0;
    logMessage("Reading file from pocketReader...");

    const readNext = () => {
      const slice  = file.slice(offset, offset + chunkSize);
      const reader = new FileReader();

      reader.onload = () => {
        offset += chunkSize;
        const percent   = Math.min(100, Math.floor((offset / file.size) * 100));
        setFetchProgress(percent);

        const milestone = Math.floor(percent / 25) * 25;
        if (milestone > lastReadMilestoneRef.current && milestone > 0) {
          lastReadMilestoneRef.current = milestone;
          logMessage(`Reading: ${milestone}%`);
        }

        if (offset < file.size) {
          readNext();
        } else {
          logMessage("File read complete");
          uploadFile(file);
        }
      };

      reader.onerror = () => logMessage("Error reading file.");
      reader.readAsArrayBuffer(slice);
    };

    readNext();
  };

 const uploadFile = (file) => {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  uploadIdRef.current = uploadId;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("serialNumber", serialNumber);
  formData.append("companyName", companyName);
  formData.append("uploadId", uploadId);
  if (workOrder.trim() !== "") {
    formData.append("workOrder", workOrder.trim());
  }

  logMessage("Sending file to server...");
  setUploadProgress(0);

  // fetch() can't observe request-body progress, so the bar sat at 0 for the
  // whole browser→server transfer (the actual slow leg) and then jumped.
  // XHR fires upload.onprogress as bytes leave the device. The bar is capped
  // at 99% until the server confirms, since after the body is sent the server
  // still has to decrypt and push to Azure (fast, but not done yet) — the
  // cloud-leg milestones stream into the log panel over WebSocket meanwhile.
  const xhr = new XMLHttpRequest();
  xhr.open("POST", `${API_URL}/upload`);

  xhr.upload.onprogress = (e) => {
    if (!e.lengthComputable) return;
    const pct = Math.min((e.loaded / e.total) * 100, 99);
    const floorPct = Math.floor(pct);
    if (floorPct > lastUploadFloorRef.current) {
      lastUploadFloorRef.current = floorPct;
      setUploadProgress(pct);
    }
  };

  const failUpload = (detail) => {
    logMessage("Upload failed: " + detail);
    alert("Upload failed — please try again.\n" + detail);
  };

  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      lastUploadFloorRef.current = 100;
      setUploadProgress(100);
    } else {
      // PSA uploads answer with JSON carrying per-leg SFTP detail; every other
      // response is plain text. Prefer the readable `message` when it is there.
      let detail = xhr.responseText || `HTTP ${xhr.status}`;
      try { detail = JSON.parse(xhr.responseText).message || detail; } catch {}
      failUpload(detail);
    }
  };

  xhr.onerror = () => failUpload("network error");

  xhr.send(formData);
};

  const handleFileSelect = () => {
    if (customFilename.trim() === "") {
      alert("No filename input.");
      return;
    }
    alert("Allow up to a minute for file to load once selected from iPad, will seem 'stuck' due to iOS.");
    if (window.hiddenFileInput) {
      // Reset so re-selecting the same file/path still fires onChange —
      // browsers only fire "change" when the input's value actually changes.
      window.hiddenFileInput.value = null;
      window.hiddenFileInput.click();
    }
  };

  return (
    <div className="file-transfer">
      <div className="ft-card">
        <div className="ft-card__header">
          <h1 className="ft-card__title">pocketReader Data Upload</h1>
        </div>
        <div className="ft-card__body">
          <button className="ft-select-btn" onClick={handleFileSelect}>
            Select the correct file from connected pocketReader
          </button>
          <input
            type="file"
            // accept=".fdr,.FDR,.tsc,.TSC,.wav,.mp4,.bin"
            ref={(ref) => (window.hiddenFileInput = ref)}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div className="ft-field">
            <label className="ft-label" htmlFor="ft-tail-number">Tail Number</label>
            <input
              id="ft-tail-number"
              className="ft-input"
              type="text"
              placeholder="Enter Tail Number"
              value={customFilename}
              onChange={(e) => setCustomFilename(e.target.value)}
            />
          </div>

          {companyName === 'MORNINGSTAR' && (
            <div className="ft-field">
              <label className="ft-label" htmlFor="ft-work-order">Work Order (optional)</label>
              <input
                id="ft-work-order"
                className="ft-input"
                type="text"
                placeholder="Work Order (optional)"
                value={workOrder}
                onChange={(e) => setWorkOrder(e.target.value)}
              />
            </div>
          )}

          {selectedFile && (
            <div className="ft-file-meta">
              <div className="ft-file-meta__item">
                <span className="ft-file-meta__key">File</span>
                <span className="ft-file-meta__val">{selectedFile.name}</span>
              </div>
              <div className="ft-file-meta__sep" />
              <div className="ft-file-meta__item">
                <span className="ft-file-meta__key">Size</span>
                <span className="ft-file-meta__val">{(selectedFile.size / 1024).toFixed(2)} KB</span>
              </div>
            </div>
          )}

          {selectedFile && (
            <div className="ft-progress-panel">
              <div className="progress-row">
                <div className="progress-row__meta">
                  <span className="progress-row__label">Reading from pocketReader</span>
                  <span className="progress-row__pct">{progress}%</span>
                </div>
                <ProgressBar barHeight={8} progressPercentage={progress} />
              </div>
              <div className="progress-row">
                <div className="progress-row__meta">
                  <span className="progress-row__label">Uploading to server</span>
                  <span className="progress-row__pct">{Math.round(displayedUploadProgress)}%</span>
                </div>
                <ProgressBar barHeight={8} progressPercentage={Math.round(displayedUploadProgress)} />
              </div>
              {Object.values(sftpLegs)
                .sort((a, b) => a.order - b.order)
                .map((leg) => (
                  <div className="progress-row" key={leg.leg}>
                    <div className="progress-row__meta">
                      <span className="progress-row__label">
                        {leg.state === "failed"
                          ? `Uploading to ${leg.leg} — failed`
                          : `Uploading to ${leg.leg}`}
                      </span>
                      <span className="progress-row__pct">{leg.percent}%</span>
                    </div>
                    <ProgressBar barHeight={8} progressPercentage={leg.percent} />
                  </div>
                ))}
            </div>
          )}

          {logs.length > 0 && (
            <details
              className="ft-log"
              open={logOpen}
              onToggle={(e) => setLogOpen(e.currentTarget.open)}
            >
              <summary className="ft-log__summary">Transmission log</summary>
              <div className="ft-log__body">
                {logs.map((log, i) => (
                  <div key={i} className="ft-log__entry">{log}</div>
                ))}
                <div ref={logEndRef} />
              </div>
            </details>
          )}

          {transferComplete && (
            <div className="ft-complete-wrap">
              <div className="ft-complete">
                <span className="ft-complete__icon">✓</span>
                <span className="ft-complete__text">Transfer Complete</span>
              </div>
              <button
                className="ft-select-btn"
                onClick={() => {
                  setSelectedFile(null);
                  setUploadProgress(0);
                  setDisplayedUploadProgress(0);
                  setFetchProgress(0);
                  setTransferComplete(false);
                  setLogs([]);
                  setCustomFilename("");
                  setWorkOrder("");
                  lastReadMilestoneRef.current = -1;
                  lastUploadFloorRef.current = -1;
                  displayedProgressRef.current = 0;
                  uploadIdRef.current = null;
                  if (window.hiddenFileInput) window.hiddenFileInput.value = null;
                }}
              >
                Select Next File
              </button>
            </div>
          )}
        </div>
      </div>

      {companyName != "Avionica" && <InstructionMenu company={companyName} instructions={instructions} />}

      <div className="space-files"></div>
    </div>
  );
};

export default FileTransfer;
